import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  AlignItems,
  Box,
  Display,
  DisplayPosition,
  FlexDirection,
  Icon,
  JustifyContent,
  Overflow,
  ZIndex,
} from '@gousto-internal/citrus-react'

import { Recipe } from '@library/menu-service'
import { RecipeClickableImage } from './components/Recipe/RecipeClickableImage'
import { getMoodImage } from './utils/formatters'
import { ViewBasketButton } from './components/ViewBasketButton/ViewBasketButton'
import { PlaceholderBanner } from './components/PlaceholderBanner/PlaceholderBanner'
import { Tooltip } from './components/Tooltip/Tooltip'

type ScrollableBasketProps = {
  isCheckoutDisabled?: boolean
  isLoading?: boolean
  onClickViewBasket: (e: SyntheticEvent) => void
  onRecipeClick: (recipeUuid: string) => void
  selectedRecipes: Recipe[]
  showTooltip: boolean
  onCloseTooltip: () => void
}

export const ScrollableBasket = ({
  isCheckoutDisabled = true,
  isLoading = true,
  onClickViewBasket,
  onRecipeClick,
  selectedRecipes,
  showTooltip,
  onCloseTooltip,
}: ScrollableBasketProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    const scrollEl = scrollRef.current
    const containerEl = containerRef.current
    if (!scrollEl || !containerEl) return

    const checkOverflow = () => {
      const scrollWidth = scrollEl.scrollWidth
      const containerWidth = containerEl.offsetWidth
      const screenWidth = window.innerWidth

      const visibleWidth = Math.min(containerWidth, screenWidth)

      setHasOverflow(scrollWidth > visibleWidth)
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [selectedRecipes])

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    // Scroll the last recipe (newest) into view
    if (selectedRecipes.length > 0) {
      const lastRecipeBox = scrollEl.lastElementChild as HTMLElement
      if (lastRecipeBox) {
        lastRecipeBox.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' })
      }
    }
  }, [selectedRecipes])

  const handleOnRecipeClick = (e: SyntheticEvent, recipeUuid: string) => {
    e.stopPropagation()
    onRecipeClick(recipeUuid)
  }

  // Scroll one recipe card at a time
  const scrollByCard = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return

    const firstChild = el.firstElementChild as HTMLElement
    const scrollAmount = firstChild ? firstChild.offsetWidth + 16 /* gap */ : 200
    const scrollBy = direction === 'left' ? -scrollAmount : scrollAmount

    el.scrollBy({ left: scrollBy, behavior: 'smooth' })
  }

  return (
    <Box
      data-testid="container"
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, FlexDirection.Row]}
      width="100%"
      justifyContent={JustifyContent.FlexEnd}
      alignItems={AlignItems.Center}
      wrap={false}
      paddingV={2}
      paddingH={[2, 5]}
      gap={2}
    >
      {selectedRecipes.length === 1 && (
        <Tooltip open={showTooltip} onClose={() => onCloseTooltip()} />
      )}
      <Box
        position={DisplayPosition.Relative}
        ref={containerRef}
        flexGrow={1}
        minWidth="0"
        maxWidth="100vw"
      >
        {hasOverflow && (
          <Box
            position={DisplayPosition.Absolute}
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            width="40px"
            zIndex={ZIndex.Top}
            height="100%"
            left="0"
            top="0"
            bottom="0"
            style={{
              background:
                'linear-gradient(to left, rgba(255,255,255,0) 0%, rgba(240,240,240,1) 100%)',
              cursor: 'pointer',
            }}
            onClick={() => scrollByCard('left')}
          >
            <Icon name="chevron_left" />
          </Box>
        )}

        <Box display={Display.Flex} justifyContent={JustifyContent.FlexEnd}>
          <Box
            ref={scrollRef}
            display={Display.Flex}
            gap={2}
            data-testid="scrollable-recipes"
            overflowX={Overflow.Scroll}
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'thin',
            }}
          >
            {selectedRecipes.length === 1 && !showTooltip && <PlaceholderBanner />}

            {selectedRecipes.map((recipe, index) => (
              <Box key={`${recipe.id}-${index}`} flexShrink={0}>
                {recipe && !isLoading && (
                  <RecipeClickableImage
                    image={getMoodImage(recipe.images)}
                    onClick={(e) => handleOnRecipeClick(e, recipe.id)}
                    name={recipe.name}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {hasOverflow && (
          <Box
            position={DisplayPosition.Absolute}
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            width="40px"
            zIndex={ZIndex.Top}
            height="100%"
            right="0"
            top="0"
            bottom="0"
            style={{
              background:
                'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(240,240,240,1) 100%)',
              cursor: 'pointer',
            }}
            onClick={() => scrollByCard('right')}
          >
            <Icon name="chevron_right" />
          </Box>
        )}
      </Box>

      <Box data-testid="viewBasketButton" flexShrink={0} width={['100%', 'auto']}>
        <ViewBasketButton
          disabled={isCheckoutDisabled}
          onClick={onClickViewBasket}
          counter={selectedRecipes.length}
        />
      </Box>
    </Box>
  )
}
